import React from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Font,
    Image,
} from "@react-pdf/renderer";
import Html from "react-pdf-html";
import toRupiah from "../../utils/toRupiah";
import toRibuan from "../../utils/toRibuan";
import OpenSans from "../../assets/fonts/OpenSans-Regular.ttf";
import OpenSansBold from "../../assets/fonts/OpenSans-Bold.ttf";
import OpenSansSemiBold from "../../assets/fonts/OpenSans-SemiBold.ttf";
import OpenSansMedium from "../../assets/fonts/OpenSans-Medium.ttf";
import UbuntuMono from "../../assets/fonts/UbuntuMono-Regular.ttf";
import { ContentUkm } from "../../constants/types";
import { StrukCartItem, StrukData } from "./strukData";
import moment from "moment";
import "moment/locale/id";

Font.register({
    family: "OpenSans",
    format: "truetype",
    src: OpenSans,
});
Font.register({
    family: "OpenSansBold",
    format: "truetype",
    src: OpenSansBold,
});
Font.register({
    family: "OpenSansSemiBold",
    format: "truetype",
    src: OpenSansSemiBold,
});
Font.register({
    family: "OpenSansMedium",
    format: "truetype",
    src: OpenSansMedium,
});
Font.register({
    family: "UbuntuMono",
    format: "truetype",
    src: UbuntuMono,
});

moment.locales();
moment.locale("id");

const stylesheet = {
    p: {
        margin: 0,
        textAlign: "center",
    },
};

type ICart = StrukCartItem;

export interface PrintStrukPdfFormat {
    is80mm: boolean;
    isIncludeDapur?: boolean;
    groupByKategori: boolean;
}

export interface PrintStrukPdfProps {
    ukm?: ContentUkm;
    data: StrukData;
    customStruk?: { html: string | null } | null;
    format: PrintStrukPdfFormat;
}

const PrintStrukPdf = ({
    ukm,
    data,
    customStruk,
    format,
}: PrintStrukPdfProps) => {
    if (!ukm) return null;
    const {
        cart,
        category,
        total,
        diskon,
        pajak,
        pajakPersen,
        serviceCustom,
        serviceCustomPersen,
        tunai,
        kembalian,
        kasir,
        sales,
        tanggalTransaksi,
        transaksiKe,
        nomorInvoice,
        namaMetode,
        noMetode,
        keterangan,
        belumLunas,
        meja,
        pelangganWebstore,
    } = data;
    const { is80mm, isIncludeDapur, groupByKategori } = format;
    const namaToko = ukm.namaToko;
    const alamatToko = ukm.alamatToko;
    const telpToko = ukm.noTelp;
    const gambarToko = ukm.gambarTokoBW ?? undefined;
    const isPro = ukm.isPro;
    const html =
        customStruk && customStruk.html !== null ? customStruk.html : undefined;
    const qr = data.qr;

    const isExtraBigSpacing = cart?.length === 1 && !keterangan;
    const isBigSpacing = cart?.length === 1 && keterangan;
    const isMediumSpacing = cart && cart.length > 1 && cart.length < 6;
    const isSmallSpacing = cart && cart.length > 5 && cart.length < 11;

    const styles = StyleSheet.create({
        page: {
            backgroundColor: "#FFF",
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 14,
            paddingRight: 14,
            height: "100%",
        },
        containerTitle: {
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            marginVertical: 12,
        },
        title: {
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            marginTop: 0,
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
            fontFamily: "OpenSansSemiBold",
            fontWeight: "bold",
        },
        subtitle: {
            fontSize: 8,
            marginTop: 0,
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
            fontFamily: "OpenSans",
        },
        cashierName: {
            fontSize: 8,
            marginTop: 6,
            fontFamily: "OpenSans",
        },
        tanggalTransaksi: {
            flexDirection: "row",
            justifyContent: "space-between",
            fontSize: 8,
            fontFamily: "OpenSans",
        },
        containerHeader: {
            flexDirection: "row",
            backgroundColor: "#45A779",
            color: "#fff",
            alignItems: "center",
            height: 24,
            textAlign: "center",
            fontStyle: "bold",
            flexGrow: 1,
        },
        namaHeader: {
            width: "45%",
            fontSize: 14,
            fontWeight: 700,
        },
        hargaHeader: {
            width: "15%",
            fontSize: 14,
            fontWeight: 700,
        },
        jumlahHeader: {
            width: "10%",
            fontSize: 14,
            fontWeight: 700,
        },
        skuHeader: {
            width: "25%",
            fontSize: 14,
            fontWeight: 700,
        },
        row: {
            fontSize: 8,
            flexDirection: "row",
            fontFamily: "OpenSans",
        },
        column: {
            marginTop: 1,
            marginBottom: 2,
            fontSize: 8,
            flexDirection: "column",
            fontFamily: "OpenSans",
        },
        columnDapur: {
            marginTop: 2,
            marginBottom: 2,
            fontSize: 8,
            flexDirection: "column",
            fontFamily: "OpenSans",
        },
        columnKategori: {
            marginTop: 1,
            marginBottom: 2,
            marginLeft: 5,
            fontSize: 8,
            flexDirection: "column",
            fontFamily: "OpenSans",
        },
        containerNamaBarang: {
            width: "100%",
            flexDirection: "column",
        },
        namaBarang: {
            textAlign: "left",
            fontSize: 8,
            marginBottom: 1,
            fontFamily: "OpenSansSemiBold",
        },
        qty: {
            width: "15%",
            textAlign: "left",
            fontSize: 8,
        },
        hargaSatuan: {
            width: "40%",
            fontSize: 8,
            textAlign: "left",
        },
        rate: {
            width: "45%",
            textAlign: "right",
            fontSize: 8,
        },
        keterangan: {
            fontSize: 8,
            marginTop: 8,
            fontFamily: "OpenSans",
        },
        divider: {
            marginVertical: 4,
            borderTop: "1px",
            color: "black",
            borderStyle: "dashed",
        },
        containerInfoTransaksi: {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            fontFamily: "OpenSans",
        },
        infoTransaksi: {
            fontSize: 8,
            width: "50%",
            textAlign: "left",
        },
        nilaiTransaksi: {
            width: "50%",
            fontSize: 8,
            textAlign: "right",
        },
        infoTransaksiBold: {
            fontSize: 8,
            fontFamily: "OpenSansSemiBold",
            width: "40%",
            textAlign: "left",
        },
        nilaiTransaksiBold: {
            width: "50%",
            fontSize: 8,
            fontFamily: "OpenSansSemiBold",
            textAlign: "right",
        },
        containerPenutup: {
            marginTop: 5,
            display: "flex",
            flexDirection: "column",
            fontSize: 8,
            fontFamily: "OpenSansSemiBold",
        },
        containerBelumLunas: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 8,
            fontFamily: "OpenSans",
        },
        penutup: {
            fontSize: 8,
            fontFamily: "OpenSansSemiBold",
        },
        containerWaterMark: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 6,
            marginTop: 2,
            marginBottom: 5,
            fontFamily: "OpenSans",
        },
        containerWaterMarkDapur: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 6,
            marginTop: isExtraBigSpacing
                ? 500
                : isBigSpacing
                ? 400
                : isMediumSpacing
                ? 300
                : isSmallSpacing
                ? 260
                : 5,
            marginBottom: 5,
            fontFamily: "Helvetica",
        },
        containerNore: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 6,
        },
        containerLogo: {
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: 12,
            marginBottom: 6,
        },
        logo: {
            width: 54,
            height: 54,
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
        },
        qr: {
            width: "3cm",
            height: "3cm",
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
        },
        boldText: {
            fontWeight: 700,
            fontFamily: "OpenSansSemiBold",
        },
        containerJatuhTempo: {
            width: "100%",
            marginTop: 10,
            marginBottom: 14,
        },
        namaKategori: {
            textAlign: "left",
            fontSize: 8,
            marginBottom: 1,
            fontWeight: 700,
            fontFamily: "OpenSansBold",
        },
        containerDapur: {
            width: "100%",
        },
    });

    const calculateTotalQty = () => {
        if (category && category.length > 0) {
            return category.reduce((total, kategori) => {
                return total + kategori.items.reduce((subTotal, item) => {
                    return subTotal + item.jumlahItem;
                }, 0);
            }, 0);
        } else if (cart && cart.length > 0) {
            return cart.reduce((sum, item) => sum + item.qty, 0);
        }
        return 0;
    };

    const rows =
        cart &&
        cart.map((row) => (
            <View style={styles.column} key={row.id}>
                <View style={{ width: "65%", flexDirection: "column" }}>
                    <Text style={styles.namaBarang}>{row.namaBarang}</Text>
                </View>
                <View style={{ flexDirection: "row" }}>
                    <Text style={styles.qty}>{row.qty}x</Text>
                    <Text style={styles.hargaSatuan}>
                        @{toRibuan(row.harga)}
                    </Text>
                    <Text style={styles.rate}>
                        {toRupiah(row.harga * row.qty)}
                    </Text>
                </View>
            </View>
        ));

    const rowk = category?.map((row, rowIndex) => {
        const totalQtyKategori = row.items.reduce((sum, item) => sum + item.jumlahItem, 0);

        return (
            <View key={rowIndex}>
                <View
                    style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        marginTop: 2,
                        marginBottom: 2,
                    }}
                >
                    <Text style={styles.namaKategori}>{row.kategori}</Text>
                    <Text style={styles.namaKategori}>{totalQtyKategori}</Text>
                </View>

                {row.items.map((item, itemIndex) => (
                    <View style={styles.columnKategori} key={`${rowIndex}-${itemIndex}`}>
                        <View style={{ width: "65%", flexDirection: "column" }}>
                            <Text style={styles.namaBarang}>{item.namaItem}</Text>
                        </View>
                        <View style={{ flexDirection: "row" }}>
                            <Text style={styles.qty}>{item.jumlahItem}x</Text>
                            <Text style={styles.hargaSatuan}>
                                @{toRibuan(item.hargaSatuan)}
                            </Text>
                            <Text style={styles.rate}>
                                {toRupiah(item.hargaSatuan * item.jumlahItem)}
                            </Text>
                        </View>
                    </View>
                ))}
                
                <View style={{ marginBottom: 8 }}></View>
            </View>
        );
    });

    const rowDapur =
        cart &&
        cart.map((row) => (
            <View style={styles.columnDapur} key={row.id}>
                <View style={{ flexDirection: "row" }}>
                    <Text style={styles.qty}>{row.qty}x</Text>
                    <Text>{row.namaBarang}</Text>
                </View>
            </View>
        ));

    const rowkDapur = category?.map((row, rowIndex) => {
        return (
            <View key={rowIndex}>
                <View style={{ width: "80%", flexDirection: "column", marginTop: 8, marginBottom: 4 }}>
                    <Text style={styles.namaKategori}>{row.kategori}</Text>
                </View>
                {row.items.map((item, itemIndex) => (
                    <View style={styles.columnKategori} key={`${rowIndex}-${itemIndex}`}>
                        <View style={{ flexDirection: "row" }}>
                            <Text style={styles.qty}>{item.jumlahItem}x</Text>
                            <Text>{item.namaItem}</Text>
                        </View>
                    </View>
                ))}
                <View style={{ marginBottom: 8 }}></View>
            </View>
        );
    });

    const rowtransaksi = () => {
        if (!cart || cart.length === 0) return null;

        const groupedByCategory = cart.reduce((acc, item) => {
            if (!acc[item.namaKategori]) {
                acc[item.namaKategori] = [];
            }
            acc[item.namaKategori].push(item);
            return acc;
        }, {} as Record<string, ICart[]>);

        return Object.entries(groupedByCategory).map(([kategori, items], index) => {
            const totalQtyKategori = items.reduce((sum, item) => sum + item.qty, 0);

            return (
                <View key={index}>
                    {/* Header Kategori */}
                    <View style={{ 
                        flexDirection: "row", 
                        justifyContent: "space-between", 
                        marginTop: 2,
                        marginBottom: 2,
                    }}>
                        <Text style={styles.namaKategori}>{kategori}</Text>
                        <Text style={styles.namaKategori}>{totalQtyKategori}</Text>
                    </View>
                    
                    {/* Items dalam kategori */}
                    {items.map((item, itemIndex) => (
                        <View style={styles.columnKategori} key={`${index}-${itemIndex}`}>
                            <View style={{ width: '65%', flexDirection: 'column' }}>
                                <Text style={styles.namaBarang}>{item.namaBarang}</Text>
                            </View>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={styles.qty}>{item.qty}x</Text>
                                <Text style={styles.hargaSatuan}>@{toRibuan(item.harga)}</Text>
                                <Text style={styles.rate}>{toRupiah(item.harga * item.qty)}</Text>
                            </View>
                        </View>
                    ))}
                    
                    {/* Spacer antar kategori */}
                    <View style={{ marginBottom: 8 }}></View>
                </View>
            );
        });
    };

    const widthInCm = 7;
    const widthInPoints = widthInCm * 28.35;

    return (
        <Document>
            {isIncludeDapur ? (
                <Page
                    wrap
                    size={{
                        width: is80mm ? widthInPoints : "5.5cm",
                        height: "100%",
                    }}
                    style={styles.page}
                >
                    <View>
                        {gambarToko ? (
                            <View style={styles.containerLogo}>
                                <Image
                                    style={styles.logo}
                                    src={gambarToko}
                                />
                            </View>
                        ) : (
                            <View></View>
                        )}
                        <View style={styles.containerTitle}>
                            <View style={styles.title}>
                                <Text>{namaToko}</Text>
                            </View>
                            <View style={styles.subtitle}>
                                <Text>{alamatToko}</Text>
                            </View>
                            <View style={styles.subtitle}>
                                <Text>+{telpToko}</Text>
                            </View>
                        </View>

                        {transaksiKe !== null && (
                            <View style={styles.tanggalTransaksi}>
                                <Text>No. Transaksi</Text>
                                <Text>{transaksiKe}</Text>
                            </View>
                        )}
                        {nomorInvoice && (
                            <View style={styles.tanggalTransaksi}>
                                <Text>No. Invoice</Text>
                                <Text>{nomorInvoice}</Text>
                            </View>
                        )}

                        <View style={styles.tanggalTransaksi}>
                            <Text>Kasir</Text>
                            <Text>{kasir}</Text>
                        </View>
                        {sales !== null ? (
                            <View style={styles.tanggalTransaksi}>
                                <Text>Sales</Text>
                                <Text>{sales}</Text>
                            </View>
                        ) : (
                            <View></View>
                        )}
                        <View style={styles.tanggalTransaksi}>
                            <Text>Tanggal</Text>
                            <Text>
                                {tanggalTransaksi}
                            </Text>
                        </View>
                        {namaMetode ? (
                            <View style={styles.tanggalTransaksi}>
                                <Text>Metode</Text>
                                <Text>{namaMetode}</Text>
                            </View>
                        ) : (
                            <View></View>
                        )}
                        {noMetode ? (
                            <View style={styles.tanggalTransaksi}>
                                <Text>No. Tujuan</Text>
                                <Text>{noMetode}</Text>
                            </View>
                        ) : (
                            <View></View>
                        )}
                        
                        {pelangganWebstore ? (
                            <View style={styles.tanggalTransaksi}>
                                <Text>Pelanggan</Text>
                                <Text>{pelangganWebstore}</Text>
                            </View>
                        ) : (
                            <View></View>
                        )}

                        {meja ? (
                            <View style={styles.tanggalTransaksi}>
                                <Text>Meja</Text>
                                <Text>{meja.ruangan} - {meja.nomor}</Text>
                            </View>
                        ) : (
                            <View></View>
                        )}

                        <View style={styles.divider}></View>

                        <View>{rowkDapur}</View>
                        <View>
                            {groupByKategori ? rowtransaksi() : rowDapur}
                        </View>

                        <View style={styles.divider}></View>

                        <View style={styles.containerWaterMarkDapur}></View>
                    </View>
                </Page>
            ) : null}
            <Page
                wrap
                size={{
                    width: is80mm ? widthInPoints : "5.5cm",
                    height: "100%",
                }}
                style={styles.page}
            >
                <View>
                    {gambarToko ? (
                        <View style={styles.containerLogo}>
                            <Image style={styles.logo} src={gambarToko} />
                        </View>
                    ) : (
                        <View></View>
                    )}
                    <View style={styles.containerTitle}>
                        <View style={styles.title}>
                            <Text>{namaToko}</Text>
                        </View>
                        <View style={styles.subtitle}>
                            <Text>{alamatToko}</Text>
                        </View>
                        <View style={styles.subtitle}>
                            <Text>+{telpToko}</Text>
                        </View>
                    </View>
                    {qr ? (
                        <View style={styles.containerLogo}>
                            <Image style={styles.qr} src={qr} />
                        </View>
                    ) : (
                        <View></View>
                    )}

                    {transaksiKe !== null && (
                        <View style={styles.tanggalTransaksi}>
                            <Text>No. Transaksi</Text>
                            <Text>{transaksiKe}</Text>
                        </View>
                    )}
                    {nomorInvoice && (
                        <View style={styles.tanggalTransaksi}>
                            <Text>No. Invoice</Text>
                            <Text>{nomorInvoice}</Text>
                        </View>
                    )}

                    <View style={styles.tanggalTransaksi}>
                        <Text>Kasir</Text>
                        <Text>{kasir}</Text>
                    </View>
                    {sales !== null ? (
                        <View style={styles.tanggalTransaksi}>
                            <Text>Sales</Text>
                            <Text>{sales}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    <View style={styles.tanggalTransaksi}>
                        <Text>Tanggal</Text>
                        <Text>
                            {tanggalTransaksi}
                        </Text>
                    </View>
                    {namaMetode ? (
                        <View style={styles.tanggalTransaksi}>
                            <Text>Metode</Text>
                            <Text>{namaMetode}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    {noMetode ? (
                        <View style={styles.tanggalTransaksi}>
                            <Text>No. Tujuan</Text>
                            <Text>{noMetode}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}

                    {pelangganWebstore ? (
                        <View style={styles.tanggalTransaksi}>
                            <Text>Pelanggan</Text>
                            <Text>{pelangganWebstore}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}

                    {meja ? (
                        <View style={styles.tanggalTransaksi}>
                            <Text>Meja</Text>
                            <Text>{meja.ruangan} - {meja.nomor}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}

                    <View style={styles.divider}></View>

                    <View>{rowk}</View>
                    <View>{groupByKategori ? rowtransaksi() : rows}</View>

                    <View style={styles.divider}></View>

                    {cart && (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>Total Qty</Text>
                            <Text style={styles.nilaiTransaksi}>
                               {calculateTotalQty()}
                            </Text>
                        </View>
                    )}

                    {diskon ? (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>Diskon</Text>
                            <Text style={styles.nilaiTransaksi}>
                                ({toRupiah(diskon)})
                            </Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    {serviceCustom ? (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>
                                {serviceCustomPersen
                                    ? `Service (${serviceCustomPersen}%)`
                                    : `Service`}
                            </Text>
                            <Text style={styles.nilaiTransaksi}>
                                {toRupiah(serviceCustom)}
                            </Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    {pajak ? (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>
                                {pajakPersen
                                    ? `Pajak (${pajakPersen}%)`
                                    : `Pajak`}
                            </Text>
                            <Text style={styles.nilaiTransaksi}>
                                {toRupiah(pajak)}
                            </Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    <View style={styles.divider}></View>
                    <View style={styles.containerInfoTransaksi}>
                        <Text style={styles.infoTransaksiBold}>Total</Text>
                        <Text style={styles.nilaiTransaksiBold}>
                            {toRupiah(total)}
                        </Text>
                    </View>
                    <View style={styles.divider}></View>
                    {tunai ? (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>
                                {namaMetode}
                            </Text>
                            <Text style={styles.nilaiTransaksi}>
                                {toRupiah(tunai)}
                            </Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    {kembalian ? (
                        <View style={styles.containerInfoTransaksi}>
                            <Text style={styles.infoTransaksi}>Kembalian</Text>
                            <Text style={styles.nilaiTransaksi}>
                                {toRupiah(kembalian)}
                            </Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    <View style={styles.divider}></View>
                    {belumLunas ? (
                        <>
                            <View style={styles.divider}></View>
                            <View style={styles.containerBelumLunas}>
                                <Text style={styles.penutup}>BELUM LUNAS</Text>
                            </View>
                            <View style={styles.divider}></View>
                        </>
                    ) : (
                        <View></View>
                    )}
                    {keterangan ? (
                        <View style={styles.keterangan}>
                            <Text>Keterangan:</Text>
                            <Text>{keterangan}</Text>
                        </View>
                    ) : (
                        <View></View>
                    )}
                    <View style={styles.containerPenutup}>
                        {html ? (
                            <Html
                                stylesheet={stylesheet}
                                style={{ fontSize: 8 }}
                            >
                                {`<div>${html}</div>`}
                            </Html>
                        ) : (
                            <>
                                <Text style={styles.penutup}>
                                    Terimakasih telah berbelanja
                                </Text>
                                <Text style={styles.penutup}>
                                    Sampai bertemu kembali
                                </Text>
                            </>
                        )}
                    </View>
                    <View style={styles.containerWaterMark}>
                        <Text>
                            {isPro !== 1 && (
                                <Text style={styles.boldText}>Free</Text>
                            )}{" "}
                            POS System by UKM Lokal
                        </Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default PrintStrukPdf;